import React, { useContext, useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import withAppContext from '../../hooks/withAppContext';
import { useHashAsState } from '../../hooks';
import ElasticMapper from '../../services/ElasticMapper';
import qs from 'qs';
import Image, { IImage } from '../Image';
import Loading from '../Loading';
import VHSBlogRollFilterBar from './VHSBlogRollFilterBar';
import VHSBlogRollTile from './VHSBlogRollTile';
import VHSBlogRollPagination from './VHSBlogRollPagination';
import Dropdown from 'bootstrap/js/dist/dropdown';

export interface IBlogAuthor {
	name?: string;
	url?: string;
	image?: IImage;
}

export interface IBlogPostCategory {
	name?: string;
	url?: string;
}

export interface IBlogPostTag {
	name?: string;
	url?: string;
}

export interface IBlogPost {
	image?: IImage;
	heading?: string;
	author?: IBlogAuthor;
	readTime?: string;
	excerpt?: string;
	content?: string;
	url?: string;
	categories?: IBlogPostCategory[];
	tags?: IBlogPostTag[];
	date?: string;
}

export interface IVHSBlogRollTiles {
	/**
	 * Content
	 * @control richtext
	 */
	content: string;
	/**
	 * Item Image
	 */
	image?: IImage;
}

export interface IVHSBlogRollProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	context?: any;
	/**
	 * @noUI
	 */
	assets?: any;
	/**
	 * @noUI
	 */
	archivePage?: string;
	/**
	 * Style
	 */
	style: 'default' | 'index' | 'search';
	tiles?: IVHSBlogRollTiles[];
	showOnlyLatestPosts?: boolean;
	numberOfPostsToShow?: number;
	showPagination?: boolean;
	/**
	 * @noUI
	 */
	posts?: {
		total: number;
		hits: any[];
	};
	/**
	 * @noUI
	 */
	fetchArgs?: any;
	/**
	 * @noUI
	 * prevent WP user from accessing this option. Keeping it here just in case
	 */
	filterBarTargetUrl?: string;
	filterSearchPlaceholder?: string;
	filterLabel?: string;
	displaySearchTaxonomies?: boolean;
	noResultsMessage?: string;
	enableStartScreen?: boolean;
	startScreenMessage?: string;
	coursesOnly?: boolean;
	courseIndexLink?: string;
	placeholderImage?: IImage;
	hasDisclaimer?: boolean;
	hidePostTagFilter?: boolean;
}

/**
 * BlogRoll
 *
 * @block
 * @icon media-document
 * @requiresHooks
 */
const VHSBlogRoll: React.FC<IVHSBlogRollProps> = withAppContext(
	({
		context,
		style,
		tiles = [],
		showOnlyLatestPosts = false,
		numberOfPostsToShow,
		showPagination = true,
		posts,
		fetchArgs,
		filterLabel,
		displaySearchTaxonomies = true,
		filterSearchPlaceholder = 'Search',
		noResultsMessage = 'No results found. Please adjust your filters.',
		enableStartScreen = true,
		startScreenMessage = 'Please enter your search above',
		coursesOnly = false,
		courseIndexLink,
		placeholderImage,
		hasDisclaimer,
		hidePostTagFilter = false,
	}) => {
		// @ts-ignore
		const { assets: contextAssets, Api } = useContext(context);
		const [assets, setAssets] = useState<any>(null);
		const [blogRoll, setBlogRoll] = useState<any>(null);
		const [termMap, setTermMap] = useState<any>(null);
		const [total, setTotal] = useState<number | null>(null);
		const [loading, setLoading] = useState<boolean>(false);
		const [startScreen, setStartScreen] = useState<boolean>(false);
		const [featuredResultsCount, setFeaturedResultsCount] = useState<number>(0);
		const taxonomy_parameters = ((taxonomies) => {
			const t: any = {};
			(taxonomies || []).forEach((tax: any) => {
				t[tax.taxonomy] = 'csv';
			});
			return t;
		})(assets?.taxonomies);
		const [state, hash, updateState, normalizeState] = useHashAsState({
			parameters: {
				page: 'int',
				...taxonomy_parameters,
				search: 'string',
				total: 'int',
			},
			defaults: {
				search: '',
				page: 1,
				total: total,
			},
		});

		// ref to pass down to pagination so it can force a scroll to top of blogRoll when user changes page.
		const topRef = useRef(null);

		useEffect(() => {
			if (assets) {
				updateState({ total: total,});
			}
		}, [total, assets]);

		// Here's the effect that actually gets the posts from the server whenever the search has changed.
		const {total: ignored, ...relevantArgs} = state;
		const relevantHash = qs.stringify(relevantArgs);
		useEffect(() => {
			if (posts) {
				setTotal(posts.total);
				setBlogRoll(ElasticMapper(posts));
				return;
			}
			if (!assets) {
				return;
			}
			let args = qs.parse(relevantHash);
			args.featured_required = "false";
			args.posts_per_page = assets?.posts_per_page;
			if (fetchArgs?.posts_per_page) {
				args.posts_per_page = fetchArgs.posts_per_page;
			}
			if (fetchArgs) {
				args = {
					...fetchArgs,
					...args,
					featured_required : false,
				};
			}

			// override whatever is in args to only show posts
			if (showOnlyLatestPosts) {
				args.post_type = 'post';
			}
			if (numberOfPostsToShow) {
				args.posts_per_page = `${numberOfPostsToShow}`; // cast to string
			}
			setBlogRoll(null);
			setLoading(true);

			// show the start screen if blogRoll is NOT set to show latest posts AND search state hasn't been changed.
			if (enableStartScreen && !showOnlyLatestPosts && (Object.keys(state).length == 3 && state.search == '' && state.page == 1)) {
				setStartScreen(true);
				setLoading(false);
			} else {
				let resetScreen = true;
				setFeaturedResultsCount(0);
				Api.getBlogRoll(args)
					.then((results: any) => {
						// clear out the featured status by looping through the results and setting featured to false
						results.hits.forEach((hit: any) => {
							hit._source.featured = false;
						});
						if (coursesOnly && Object.keys(state).length == 3 && state.search == '' && state.page == 1) {
							resetScreen = false;
							args.featured_required = "true";
							Api.getBlogRoll(args).then((results2: any) => {
								setTotal( results.total + results2.total);
								setFeaturedResultsCount(results2.total);
								// sort the results2 by a numeric version of grade descending, then post title ascending
								results2.hits.sort((a: any, b: any) => {
									const gradeA = parseInt(a._source.grade[0].name);
									const gradeB = parseInt(b._source.grade[0].name);
									if (gradeA == gradeB) {
										return a._source.code.localeCompare(b._source.code);
									}
									return gradeB - gradeA;
								});

								setBlogRoll(ElasticMapper(results2).concat(ElasticMapper(results)));
							})
							.finally( () => {
								setLoading(false);
								setStartScreen(false);
							});
						} else {
							resetScreen = true;
							setTotal(results.total);
							setBlogRoll(ElasticMapper(results));
						}
					})
					.finally(() => {
						if ( resetScreen ) {
							setLoading(false);
							setStartScreen(false);
						}
					});
			}
		}, [
			relevantHash,
			assets,
			fetchArgs,
			posts,
			showOnlyLatestPosts,
			numberOfPostsToShow,
		]);

		useEffect(() => {
			const assetReqArgs = { ...fetchArgs };
			const setAssetsAndTermMap = (results: any) => {
				setAssets(results);
				const map: any = {};
				if (results.taxonomies) {
					results.taxonomies.forEach((taxonomy: any) => {
						taxonomy.terms.forEach((term: any) => {
							term.taxonomy = taxonomy.taxonomy;
							map[term.term_id] = term;
						});
					});
				}
				setTermMap(map);
			};
			if (contextAssets) {
				setAssetsAndTermMap(contextAssets);
			} else {
				// override whatever is in fetchArgs to only show posts
				if (showOnlyLatestPosts) {
					assetReqArgs.post_type = 'post';
				}
				Api.getBlogRollAssets(assetReqArgs).then((results: any) => {
					setAssetsAndTermMap(results);
				});
			}

			if (!topRef.current) {
				return;
			}

			const initDropdown = () => {
				const dropdownTriggerList = [].slice.call(
					topRef.current.querySelectorAll('.dropdown'),
				);

				dropdownTriggerList.map(function (dropdownTriggerEl) {
					return new Dropdown(dropdownTriggerEl);
				});
			};

			initDropdown();
		}, [contextAssets, Api, fetchArgs]);

		if (!assets) {
			return (
				<div className="loading-wrapper">
					<Loading />
				</div>
			);
		}

		return (
			<div
				className={cn(
					'vhs-blogroll',
					(style === 'index' || !style) && 'vhs-blogroll--index',
					(style === 'search' || !style) && 'vhs-blogroll--search',
					(hasDisclaimer) && 'vhs-blogroll--disclaimer',
				)}
				ref={topRef}
			>
				<div className="vhs-blogroll__container">
					<VHSBlogRollFilterBar
						SearchPlaceholder={filterSearchPlaceholder}
						searchTaxonomies={displaySearchTaxonomies}
						filterLabel={filterLabel}
						filters={state}
						assets={assets}
						onChange={updateState}
						hidePostTagFilter={hidePostTagFilter}
					/>
					<div className="vhs-blogroll__tiles">
						{tiles.map(({ content, image }, index) => {
							if (index < 2) {
								return (
									<div className="vhs-blogroll__tile" key={index}>
										<div className="vhs-blogroll__tile-content" dangerouslySetInnerHTML={{__html: content}}/>
										{image?.src && (
											<div className="vhs-blogroll__tile-image">
												<Image {...image} className="img-fluid" />
											</div>
										)}
									</div>
								);
							}
						})}
					</div>
					<div className="vhs-blogroll__results">
						{loading ? (
							<div className="vhs-blogroll__results-loading">
								<Loading />
							</div>
						) : (
							<>
								{startScreen ? (
									<div className="vhs-blogroll__results-start">
										{startScreenMessage}
									</div>
								) : (
									<>
										{total ? (
											<>
												<div className="vhs-blogroll__items">
													{(blogRoll || []).map(
														(blogroll: any) => (
															<VHSBlogRollTile
																key={blogroll.ID + '_' + blogroll.featured}
																blogroll={blogroll}
																termMap={termMap}
																coursesOnly={coursesOnly}
																courseIndexLink={courseIndexLink}
																placeholderImage={placeholderImage}
																topRef={topRef}
															/>
														),
													)}
												</div>
												{showPagination &&
													!numberOfPostsToShow && (
														<VHSBlogRollPagination
															className="vhs-blogroll__pagination"
															page={state.page || 1}
															perPage={fetchArgs?.posts_per_page || assets?.posts_per_page}
															total={total-featuredResultsCount}
															onChange={(page: any) => updateState({page})}
															showAllLabel={false}
															topRef={topRef}
														/>
													)}
											</>
										) : (
											<p className="vhs-blogroll__results-none">
												{noResultsMessage}
											</p>
										)}
									</>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		);
	},
);
export default VHSBlogRoll;
